import { CONTACT_URL } from '@constants';
import { Alert, AlertProps, Dialog, Divider, Link, Stack, Typography } from '@mui/material';
import { Trans } from 'react-i18next';

import { resolveUrl } from '~/utils/urlUtils';

interface ErrorDialogProps {
  open: boolean;
  body: string | React.ReactNode;
  primaryAction?: React.ReactNode;
  title?: string | React.ReactNode;
  alertBox?: false | { textKey: string; severity: AlertProps['severity'] };
  footerLink?: false | { label: React.ReactNode; href: string };
  errorKey?: string | null;
}

const DEFAULT_TITLE = <Trans i18nKey="errorDialog.title" />;
const DEFAULT_ALERT = {
  textKey: 'errorDialog.alertMessage',
  severity: 'warning' as AlertProps['severity'],
};
const DEFAULT_LINK = {
  label: <Trans i18nKey="errorDialog.actions.contactSupport" />,
  href: resolveUrl(CONTACT_URL),
};

export default function ErrorDialog({ open, body, primaryAction, title, alertBox, footerLink, errorKey }: ErrorDialogProps) {
  const isCommonError = errorKey && errorKey.startsWith('common.errors.');

  title = isCommonError ? DEFAULT_TITLE : (title ?? DEFAULT_TITLE);
  body = isCommonError ? <Trans i18nKey={errorKey} /> : body;
  alertBox = alertBox ?? (isCommonError ? DEFAULT_ALERT : false);
  footerLink = footerLink ?? (isCommonError ? DEFAULT_LINK : false);

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <Stack sx={{ gap: 3 }}>
        <Stack sx={{ gap: 2, width: '100%', alignItems: 'normal' }}>
          <Typography variant="h5">{title}</Typography>
          <Divider />
          <Typography>{body}</Typography>
          {alertBox && (
            <Alert severity={alertBox.severity}>
              <Trans i18nKey={alertBox.textKey} />
            </Alert>
          )}
        </Stack>
        <Stack sx={{ gap: 3, width: '100%' }}>
          {primaryAction}
          {footerLink && (
            <Link href={footerLink.href} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 900 }}>
              {footerLink.label}
            </Link>
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
}
